import type { RouteRecordRaw, RouteRecordSingleView } from 'vue-router'

/**
 * An affiliate institution's route tree (2026-10-22).
 *
 * Every route carries `workspace: 'institution'`, so the workspace guard turns away anyone the
 * session does not give the workspace to, and the one `institution.access` capability the
 * server also enforces on every request (`IsInstitutionMember`). The analytics page belongs to
 * the insights module, like every workspace's, and is mounted here with this workspace's meta.
 *
 * **Entry weight.** This table is in the entry every marketplace visitor downloads, so it is
 * written as the Support workspace's is: the slug is spelled here rather than imported from
 * `nav.ts` (`institution-guards.spec.ts` asserts it equals `CAPABILITIES.access`), and every
 * component comes through `pages/load.ts`, so the entry holds one small import.
 */
const load = () => import('./pages/load').then((m) => m.loaders)
const page = (name: keyof typeof import('./pages')) => () =>
  load()
    .then((loaders) => loaders.pages())
    .then((m) => m[name])
const analyticsPage = () => load().then((loaders) => loaders.analytics())

/** A title key; a bare name is the workspace's own (`institution.nav.<name>`). */
type Row = [
  path: string,
  name: string,
  component: RouteRecordSingleView['component'],
  title: string,
  viewParam?: boolean,
]

const ROWS: Row[] = [
  ['', 'overview', page('InstitutionOverviewPage'), 'overview'],
  ['/analytics/:section?', 'analytics', analyticsPage, 'analytics', true],
  ['/properties', 'properties', page('InstitutionPropertiesPage'), 'properties'],
  ['/properties/:id(\\d+)', 'property', page('InstitutionPropertyPage'), 'properties'],
  ['/tenants', 'tenants', page('InstitutionTenantsPage'), 'tenants'],
  ['/payments', 'payments', page('InstitutionPaymentsPage'), 'payments'],
  ['/requests', 'requests', page('InstitutionRequestsPage'), 'requests'],
  ['/officers', 'officers', page('InstitutionOfficersPage'), 'officers'],
  ['/profile', 'profile', page('InstitutionProfilePage'), 'nav.profile'],
]

export const institutionRoutes: RouteRecordRaw[] = ROWS.map(
  ([path, name, component, title, viewParam]) => ({
    path: `/app/institution${path}`,
    name: `institution-${name}`,
    component,
    meta: {
      layout: 'dashboard',
      requiresAuth: true,
      workspace: 'institution',
      capability: 'institution.access',
      title: title.includes('.') ? title : `institution.nav.${title}`,
      ...(viewParam ? { viewParam: true } : {}),
    },
  }),
)
